import { Button } from '@/components/ui/button'
import * as Field from '@/components/ui/field'
import { ChoiceGroup, FilterSection } from '@components/FilterControls'
import { PageSearch } from '@components/PageSearch'
import { pill } from '@features/communityContent/topics'
import { useRecordings } from '@state/recordings/state'
import { RotateCcw } from 'lucide-solid'
import { For } from 'solid-js'
import { useService } from 'solid-services'
import { Flex, Grid } from 'styled-system/jsx'
import { type RecordingPeriod, type RecordingSort, useState } from './state'

const PERIODS: { value: RecordingPeriod; label: string }[] = [
  { value: 'any', label: 'Any time' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '3 months' },
  { value: '1y', label: 'Year' },
]

const SORTS: { value: RecordingSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
]

export const groupKey = (row: { source_type: string; filter_value: string }) => `${row.source_type}:${row.filter_value}`
export const groupLabel = (name: string) => (name === 'General' ? 'Uncategorized' : name)

export default function Filters(props: { onDone: () => void }) {
  const { discoveryRows } = useService(useRecordings)()
  const { filters, patchFilters, clearFilters, searchInput, setSearchInput } = useService(useState)()

  return (
    <Flex h='100%' w='100%' flexDirection='column' justifyContent='space-between' gap='6'>
      <Grid gap='6'>
        <FilterSection title='Search'>
          <Field.Root>
            <Field.Label>Keyword</Field.Label>
            <PageSearch value={searchInput()} onInput={setSearchInput} placeholder='Title, description or group…' />
          </Field.Root>
        </FilterSection>

        <FilterSection title='Group'>
          <Flex gap='7px' flexWrap='wrap'>
            <button type='button' class={pill(filters().group === null)} onClick={() => patchFilters({ group: null })}>
              All groups
            </button>
            <For each={discoveryRows()}>
              {(row) => (
                <button
                  type='button'
                  class={pill(filters().group === groupKey(row))}
                  onClick={() => patchFilters({ group: filters().group === groupKey(row) ? null : groupKey(row) })}
                >
                  {groupLabel(row.group_name)}
                </button>
              )}
            </For>
          </Flex>
        </FilterSection>

        <FilterSection title='When'>
          <ChoiceGroup label='Recorded' options={PERIODS} value={filters().period} onChange={(period) => patchFilters({ period })} />
          <ChoiceGroup
            label='Show'
            options={[{ value: 'all', label: 'All recordings' }, { value: 'evergreen', label: 'Evergreen only' }] as const}
            value={filters().evergreenOnly ? 'evergreen' : 'all'}
            onChange={(v) => patchFilters({ evergreenOnly: v === 'evergreen' })}
          />
        </FilterSection>

        <FilterSection title='Order'>
          <ChoiceGroup label='Sort by date' options={SORTS} value={filters().sort} onChange={(sort) => patchFilters({ sort })} />
        </FilterSection>
      </Grid>

      <Flex justifyContent='space-between' position='sticky' bottom='0' py='3' bg='gray.surface.bg' borderTopWidth='1px' borderColor='border'>
        <Button variant='outline' colorPalette='tscGreen' onClick={clearFilters}>
          Reset
          <RotateCcw />
        </Button>
        <Button variant='solid' colorPalette='jade' onClick={props.onDone}>
          Show results
        </Button>
      </Flex>
    </Flex>
  )
}
